/**
 * Configuración centralizada de la API.
 * Contiene la URL base y los endpoints disponibles según OpenAPI spec.
 *
 * Usa environment para cambiar entre entornos automáticamente.
 */

import { environment } from '../../../environments/environment';

export const API_BASE_URL = environment.api.baseUrl;

/**
 * Endpoints de la API organizados por dominio.
 * Cada endpoint corresponde a una operación definida en openapi.yaml.
 */
export const ApiEndpoints = {
  // ---------------------------------------------------------------------------
  // Sesiones (Sessions)
  // ---------------------------------------------------------------------------
  sessions: {
    /** POST - Iniciar sesión (CreateSessionOperation) */
    login: `${API_BASE_URL}/sessions`,
    /** POST - Revocar sesión (RevokeSessionOperation) */
    logout: `${API_BASE_URL}/sessions/revoke`,
    /** POST - Refrescar sesión (RefreshSessionOperation) */
    refresh: `${API_BASE_URL}/sessions/refresh`,
  },

  // ---------------------------------------------------------------------------
  // Cuentas (Accounts)
  // ---------------------------------------------------------------------------
  accounts: {
    /** POST - Crear cuenta (CreateAccountOperation) */
    /** GET - Listar cuentas (ListAccountOperation) */
    base: `${API_BASE_URL}/accounts`,
    /** GET/PUT/DELETE - Obtener/Editar/Eliminar cuenta por ID */
    byId: (id: string) => `${API_BASE_URL}/accounts/${id}`,
    /** PUT - Activar cuenta (ActivateAccountOperation) */
    unblock: (id: string) => `${API_BASE_URL}/accounts/${id}/unblock`,
    /** PUT - Desactivar cuenta (DeactivateAccountOperation) */
    block: (id: string) => `${API_BASE_URL}/accounts/${id}/block`,
    /** PUT - Cambiar contraseña de cuenta (ChangeAccountPasswordOperation) */
    changePassword: (id: string) => `${API_BASE_URL}/accounts/${id}/set-password`,
    /** GET/PUT - Obtener/Editar mi cuenta (GetMeAccountOperation/EditMeAccountOperation) */
    me: `${API_BASE_URL}/accounts/me`,
    /** PUT - Cambiar mi contraseña (ChangeMeAccountPasswordOperation) */
    meChangePassword: `${API_BASE_URL}/accounts/me/change-password`,
  },

  // ---------------------------------------------------------------------------
  // Usuarios (Users)
  // ---------------------------------------------------------------------------
  users: {
    /** POST - Crear usuario (CreateUserOperation) */
    /** GET - Buscar usuarios (SearchUsersOperation) */
    base: `${API_BASE_URL}/users`,
    /** GET/PUT/DELETE - Obtener/Actualizar/Eliminar usuario por ID */
    byId: (id: string) => `${API_BASE_URL}/users/${id}`,
    /** POST - Desbloquear usuario (UnblockUserOperation) */
    unblock: (id: string) => `${API_BASE_URL}/users/${id}/unblock`,
    /** POST - Bloquear usuario (BlockUserOperation) */
    block: (id: string) => `${API_BASE_URL}/users/${id}/block`,
    /** POST - Establecer contraseña de usuario (SetUserPasswordOperation) */
    changePassword: (id: string) => `${API_BASE_URL}/users/${id}/set-password`,
    /** GET/PUT - Obtener/Actualizar mi usuario (GetMeUserOperation/UpdateMeUserOperation) */
    me: `${API_BASE_URL}/users/me`,
    /** POST - Cambiar mi contraseña (ChangeMeUserPasswordOperation) */
    meChangePassword: `${API_BASE_URL}/users/me/change-password`,
  },

  // ---------------------------------------------------------------------------
  // Países (Countries)
  // ---------------------------------------------------------------------------
  countries: {
    /** POST - Crear país (CreateCountryOperation) */
    /** GET - Buscar países (SearchCountriesOperation) */
    base: `${API_BASE_URL}/countries`,
    /** GET/PUT/DELETE - Obtener/Actualizar/Eliminar país por ID */
    byId: (id: string) => `${API_BASE_URL}/countries/${id}`,
    /** GET - Buscar formularios en un país */
    forms: (countryId: string) => `${API_BASE_URL}/countries/${countryId}/forms`,
    /** GET/PUT/DELETE - Obtener/Asignar/Desasignar formulario de un país */
    formById: (countryId: string, formId: string) =>
      `${API_BASE_URL}/countries/${countryId}/forms/${formId}`,
  },

  // ---------------------------------------------------------------------------
  // Partners
  // ---------------------------------------------------------------------------
  partners: {
    /** GET - Buscar partners (SearchPartnersOperation) */
    base: `${API_BASE_URL}/partners`,
    /** GET/PUT/DELETE - Obtener/Actualizar/Eliminar partner por ID */
    byId: (id: string) => `${API_BASE_URL}/partners/${id}`,
    /** POST - Crear partner dentro de un country (CreatePartnerOperation) */
    createInCountry: (countryId: string) => `${API_BASE_URL}/countries/${countryId}/partners`,
    /** GET - Buscar formularios en un partner */
    forms: (partnerId: string) => `${API_BASE_URL}/partners/${partnerId}/forms`,
    /** GET/PUT/DELETE - Obtener/Asignar/Desasignar formulario de un partner */
    formById: (partnerId: string, formId: string) =>
      `${API_BASE_URL}/partners/${partnerId}/forms/${formId}`,
  },

  // ---------------------------------------------------------------------------
  // Beneficiarios (Beneficiaries)
  // ---------------------------------------------------------------------------
  beneficiaries: {
    /** POST - Crear beneficiario (CreateBeneficiaryOperation) */
    /** GET - Listar beneficiarios (ListBeneficiaryOperation) */
    base: `${API_BASE_URL}/beneficiaries`,
    /** GET/PUT/DELETE - Obtener/Actualizar/Eliminar beneficiario por ID */
    byId: (id: string) => `${API_BASE_URL}/beneficiaries/${id}`,
    /** POST - Crear beneficiario dentro de un partner */
    createInPartner: (partnerId: string) => `${API_BASE_URL}/partners/${partnerId}/beneficiaries`,
    /** GET/PUT/DELETE - GDPR del beneficiario */
    gdpr: (beneficiaryId: string) => `${API_BASE_URL}/beneficiaries/${beneficiaryId}/gdpr`,
    /** GET - Buscar formularios del beneficiario */
    forms: (beneficiaryId: string) => `${API_BASE_URL}/beneficiaries/${beneficiaryId}/forms`,
    /** GET - Obtener formulario del beneficiario */
    formById: (beneficiaryId: string, formId: string) =>
      `${API_BASE_URL}/beneficiaries/${beneficiaryId}/forms/${formId}`,
    /** PUT - Responder formulario (guardar borrador) */
    formResponse: (beneficiaryId: string, formId: string) =>
      `${API_BASE_URL}/beneficiaries/${beneficiaryId}/forms/${formId}/response`,
    /** POST - Enviar/finalizar respuesta del formulario */
    formResponseSubmit: (beneficiaryId: string, formId: string) =>
      `${API_BASE_URL}/beneficiaries/${beneficiaryId}/forms/${formId}/response/submit`,
  },

  // ---------------------------------------------------------------------------
  // Archivos (Files)
  // ---------------------------------------------------------------------------
  files: {
    /** POST - Subir archivo (UploadFileOperation) */
    base: `${API_BASE_URL}/files`,
    /** GET/DELETE - Obtener/Eliminar archivo por ID */
    byId: (id: string) => `${API_BASE_URL}/files/${id}`,
  },

  // ---------------------------------------------------------------------------
  // Formularios (Forms)
  // ---------------------------------------------------------------------------
  forms: {
    /** POST - Crear formulario (CreateFormOperation) */
    /** GET - Buscar formularios (SearchFormsOperation) */
    base: `${API_BASE_URL}/forms`,
    /** GET/PUT/DELETE - Obtener/Actualizar/Eliminar formulario por ID */
    byId: (id: string) => `${API_BASE_URL}/forms/${id}`,
    /** POST - Finalizar formulario (FinalizeFormOperation) */
    finalize: (id: string) => `${API_BASE_URL}/forms/${id}/finalize`,
    /** GET - Obtener formulario de un country por ID */
    byCountryId: (countryId: string, formId: string) =>
      `${API_BASE_URL}/countries/${countryId}/forms/${formId}`,
    /** PUT - Publicar formulario en país */
    countryFormPublication: (countryId: string, formId: string) =>
      `${API_BASE_URL}/countries/${countryId}/forms/${formId}/publication`,
    /** GET - Obtener formulario de un partner por ID */
    byPartnerId: (partnerId: string, formId: string) =>
      `${API_BASE_URL}/partners/${partnerId}/forms/${formId}`,
    /** PUT - Publicar formulario en partner */
    partnerFormPublication: (partnerId: string, formId: string) =>
      `${API_BASE_URL}/partners/${partnerId}/forms/${formId}/publication`,
  },

  // ---------------------------------------------------------------------------
  // Encuestas (Surveys) - NOTA: No definido en OpenAPI actual, puede ser legacy
  // ---------------------------------------------------------------------------
  surveys: {
    /** POST - Crear encuesta / GET - Listar encuestas */
    base: `${API_BASE_URL}/surveys`,
    /** GET/PUT/DELETE - Obtener/Actualizar/Eliminar encuesta por ID */
    byId: (id: string) => `${API_BASE_URL}/surveys/${id}`,
    /** PUT - Activar encuesta */
    activate: (id: string) => `${API_BASE_URL}/surveys/${id}/activate`,
    /** PUT - Desactivar encuesta */
    deactivate: (id: string) => `${API_BASE_URL}/surveys/${id}/deactivate`,
  },

  // ---------------------------------------------------------------------------
  // Apache Superset (dashboard embebido)
  // ---------------------------------------------------------------------------
  superset: {
    /** POST - Emitir guest token para el dashboard embebido (RLS por país/partner) */
    guestToken: `${API_BASE_URL}/superset/guest_token`,
  },
} as const;
